import { DatabaseCourseService } from './DatabaseCourseService';
import { EnhancedDatabaseService } from './EnhancedDatabaseService';
import { getAllCoursesUnified } from '@/utils/allCourses';
import { Course } from '@/hooks/useCourses';
import { Module } from '@/types/courseTypes';
import { logger } from '@/utils/logger';

/**
 * Unified Course Service - Seamlessly handles database and static content
 * with fallback logic to prevent Application Errors
 */
export class UnifiedCourseService {
  private static readonly FALLBACK_ENABLED = true;
  
  /**
   * Get all courses with database-first approach and static fallback
   */
  static async getAllCourses(): Promise<Course[]> {
    try {
      const dbCourses = await EnhancedDatabaseService.getAllCoursesCached();
      
      if (dbCourses && dbCourses.length > 0) {
        logger.info(`Loaded ${dbCourses.length} courses from database`);
        return dbCourses;
      }
      
      return this.getFallbackCourses();
    } catch (error) {
      logger.warn('Database courses failed, using fallback:', error);
      return this.getFallbackCourses();
    }
  }

  /**
   * Get courses by category with fallback
   */
  static async getCoursesByCategory(category: string): Promise<Course[]> {
    try {
      const dbCourses = await EnhancedDatabaseService.getCoursesByCategoryCached(category);
      
      if (dbCourses && dbCourses.length > 0) {
        return dbCourses;
      }
      
      return this.getFallbackCoursesByCategory(category);
    } catch (error) {
      logger.warn(`Database courses by category failed for ${category}, using fallback:`, error);
      return this.getFallbackCoursesByCategory(category);
    }
  }

  /**
   * Get course by ID with modules (database-first with fallback)
   */
  static async getCourseById(courseId: string): Promise<(Course & { modules: Module[] }) | null> {
    try {
      logger.info(`🔍 [UnifiedCourseService] Fetching course: ${courseId}`);
      const dbCourse = await EnhancedDatabaseService.getCourseWithContent(courseId);
      
      if (dbCourse) {
        const moduleCount = dbCourse.modules?.length || 0;
        logger.info(`✅ [UnifiedCourseService] Loaded course "${dbCourse.title}" with ${moduleCount} modules from database`);
        return dbCourse;
      }
      
      logger.warn(`⚠️ [UnifiedCourseService] Course ${courseId} not found in database, attempting static fallback`);
      const fallbackCourse = this.getFallbackCourseById(courseId);
      
      if (fallbackCourse) {
        logger.info(`✅ [UnifiedCourseService] Loaded course "${fallbackCourse.title}" from static fallback`);
      } else {
        logger.error(`❌ [UnifiedCourseService] Course ${courseId} not found in database OR static fallback - course does not exist`);
      }
      
      return fallbackCourse;
    } catch (error) {
      logger.error(`❌ [UnifiedCourseService] Database error for ${courseId}:`, error);
      logger.info(`🔄 [UnifiedCourseService] Falling back to static content for ${courseId}`);
      return this.getFallbackCourseById(courseId);
    }
  }

  /**
   * Get course modules with fallback
   */
  static async getCourseModules(courseId: string): Promise<Module[]> {
    try {
      const dbModules = await DatabaseCourseService.getCourseModules(courseId);
      
      if (dbModules && dbModules.length > 0) {
        return dbModules;
      }
      
      return this.getFallbackModules(courseId);
    } catch (error) {
      logger.warn(`Database modules failed for ${courseId}, using fallback:`, error);
      return this.getFallbackModules(courseId);
    }
  }

  /**
   * Get complete module with fallback
   */
  static async getCompleteModule(moduleId: string, courseId: string): Promise<Module | null> {
    try {
      const dbModule = await EnhancedDatabaseService.getCompleteModuleCached(moduleId, courseId);
      
      if (dbModule) {
        return dbModule;
      }
      
      return this.getFallbackModule(moduleId, courseId);
    } catch (error) {
      logger.warn(`Database module failed for ${courseId}/${moduleId}, using fallback:`, error);
      return this.getFallbackModule(moduleId, courseId);
    }
  }

  /**
   * Search courses with fallback
   */
  static async searchCourses(query: string, filters?: any): Promise<Course[]> {
    try {
      if (filters) {
        return await EnhancedDatabaseService.searchCoursesEnhanced(query, filters);
      } else {
        return await DatabaseCourseService.searchCourses(query);
      }
    } catch (error) {
      logger.warn(`Database search failed for "${query}", using fallback:`, error);
      return this.getFallbackSearchResults(query);
    }
  }

  // === FALLBACK METHODS ===

  private static getFallbackCourses(): Course[] {
    if (!this.FALLBACK_ENABLED) return [];
    
    const staticCourses = getAllCoursesUnified();
    return this.transformStaticToDbFormat(staticCourses);
  }

  private static getFallbackCoursesByCategory(category: string): Course[] {
    if (!this.FALLBACK_ENABLED) return [];
    
    const staticCourses = getAllCoursesUnified();
    const filtered = staticCourses.filter(course => 
      course.courseType === category || course.type === category
    );
    return this.transformStaticToDbFormat(filtered);
  }

  private static getFallbackCourseById(courseId: string): (Course & { modules: Module[] }) | null {
    if (!this.FALLBACK_ENABLED) return null;
    
    const staticCourses = getAllCoursesUnified();
    const course = staticCourses.find(c => c.id === courseId);
    
    if (course) {
      const transformedCourse = this.transformStaticToDbFormat([course])[0];
      // Ensure modules are included in fallback too
      return {
        ...transformedCourse,
        modules: course.modules || []
      };
    }
    
    return null;
  }

  private static getFallbackModules(courseId: string): Module[] {
    if (!this.FALLBACK_ENABLED) return [];
    
    const staticCourses = getAllCoursesUnified();
    const course = staticCourses.find(c => c.id === courseId);
    
    // Filter out known disabled modules
    const disabledModuleIds = ['openai-api-mastery'];
    const modules = course?.modules || [];
    
    return modules.filter(m => !disabledModuleIds.includes(m.id));
  }

  private static getFallbackModule(moduleId: string, courseId: string): Module | null {
    if (!this.FALLBACK_ENABLED) return null;
    
    const modules = this.getFallbackModules(courseId);
    return modules.find(m => m.id === moduleId) || null;
  }

  private static getFallbackSearchResults(query: string): Course[] {
    if (!this.FALLBACK_ENABLED) return [];
    
    const staticCourses = getAllCoursesUnified();
    const searchTerm = query.toLowerCase();
    
    const filtered = staticCourses.filter(course =>
      course.title?.toLowerCase().includes(searchTerm) ||
      course.description?.toLowerCase().includes(searchTerm)
    );
    
    return this.transformStaticToDbFormat(filtered);
  }

  /**
   * Transform static course data to database format
   */
  private static transformStaticToDbFormat(staticCourses: any[]): Course[] {
    return staticCourses.map(course => ({
      id: course.id,
      title: course.title,
      description: course.description || 'No description available',
      category: course.courseType || course.type || 'general',
      icon: null,
      route: `/courses/${course.id}`,
      level: course.level || 'intermediate',
      duration_hours: this.extractHours(course.duration || course.totalDuration || '2 hours'),
      total_modules: course.modules?.length || 0,
      is_active: true,
      prerequisites: course.prerequisites || [],
      learning_outcomes: course.learningOutcomes || course.objectives || [],
      metadata: { 
        source: 'static_fallback', 
        courseType: course.courseType || course.type,
        originalData: course
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      tags: course.tags || [],
      image_url: course.image || course.imageSrc || null,
      difficulty_rating: this.getDifficultyRating(course.level),
      popularity_score: 0,
      modules: course.modules || [] // Ensure modules are always present
    }));
  }

  private static extractHours(duration: string): number {
    const match = duration.match(/(\d+)/);
    return match ? parseInt(match[1]) : 2;
  }

  private static getDifficultyRating(level: string): number {
    switch (level?.toLowerCase()) {
      case 'beginner': return 1;
      case 'intermediate': return 2;
      case 'advanced': return 3;
      default: return 2;
    }
  }

  /**
   * Health check for service availability
   */
  static async healthCheck(): Promise<{
    database: boolean;
    static: boolean;
    recommended: 'database' | 'static';
  }> {
    let databaseAvailable = false;
    let staticAvailable = false;

    try {
      const dbCourses = await DatabaseCourseService.getAllCourses();
      databaseAvailable = dbCourses.length > 0;
    } catch (error) {
      logger.warn('Database health check failed:', error);
    }

    try {
      const staticCourses = getAllCoursesUnified();
      staticAvailable = staticCourses.length > 0;
    } catch (error) {
      logger.warn('Static courses health check failed:', error);
    }

    return {
      database: databaseAvailable,
      static: staticAvailable,
      recommended: databaseAvailable ? 'database' : 'static'
    };
  }

  
  // Legacy compatibility methods - now static
  static getAllCoursesSync(): UnifiedCourse[] {
    const staticCourses = getAllCoursesUnified();
    return staticCourses.map(course => ({
      id: course.id,
      title: course.title,
      description: course.description || '',
      level: course.level || 'intermediate',
      totalDuration: course.duration || course.totalDuration || '2 hours',
      image: course.image || course.imageSrc || '',
      prerequisites: course.prerequisites || [],
      learningOutcomes: course.learningOutcomes || course.objectives || [],
      modules: course.modules || [],
      type: course.courseType || course.type || 'general'
    }));
  }

  static async validateIntegration(): Promise<{ status: string; issues: string[]; isValid: boolean }> {
    try {
      const health = await this.healthCheck();
      const issues: string[] = [];
      
      if (!health.database) {
        issues.push('Database connection failed');
      }
      
      if (!health.static) {
        issues.push('Static courses unavailable');
      }
      
      return {
        status: issues.length === 0 ? 'healthy' : 'warning',
        issues,
        isValid: issues.length === 0
      };
    } catch (error) {
      return {
        status: 'error',
        issues: ['Integration validation failed'],
        isValid: false
      };
    }
  }

  static async getCourseStatistics(): Promise<{ 
    total: number; 
    byCategory: Record<string, number>;
    totalCourses: number;
    coursesWithModules: number;
    totalModules: number;
    totalTypes: number;
    coursesByType: Record<string, number>;
    lastUpdated: string;
  }> {
    try {
      const courses = await this.getAllCourses();
      const byCategory: Record<string, number> = {};
      const coursesByType: Record<string, number> = {};
      let totalModules = 0;
      let coursesWithModules = 0;
      
      courses.forEach(course => {
        byCategory[course.category] = (byCategory[course.category] || 0) + 1;
        coursesByType[course.level] = (coursesByType[course.level] || 0) + 1;
        
        if (course.total_modules > 0) {
          totalModules += course.total_modules;
          coursesWithModules++;
        }
      });
      
      return {
        total: courses.length,
        byCategory,
        totalCourses: courses.length,
        coursesWithModules,
        totalModules,
        totalTypes: Object.keys(coursesByType).length,
        coursesByType,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      return {
        total: 0,
        byCategory: {},
        totalCourses: 0,
        coursesWithModules: 0,
        totalModules: 0,
        totalTypes: 0,
        coursesByType: {},
        lastUpdated: new Date().toISOString()
      };
    }
  }
}

// Legacy exports for backward compatibility
export const getAllUnifiedCourses = () => getAllCoursesUnified();
export const unifiedCourseService = UnifiedCourseService;

// Legacy interface for backward compatibility
export interface UnifiedCourse {
  id: string;
  title: string;
  description: string;
  level: string;
  totalDuration: string;
  image: string;
  prerequisites: string[];
  learningOutcomes: string[];
  modules: any[];
  type: string;
}